// EntitlementContext — Cluster C.3.
//
// Single global provider that owns the entitlement-decision modal state.
// Any component calls `useEntitlementGate().check({service_sku, pincode})`
// and gets back a promise that resolves to:
//
//   true   → user is allowed; proceed with the original booking flow.
//   false  → user is NOT proceeding with this flow. Either:
//              - they hit a wall and dismissed the modal,
//              - they took the à la carte path (modal navigates away),
//              - they're going to the plans page to upgrade.
//
// The promise pattern lets booking pages do a one-line guard:
//   const allowed = await check({service_sku});
//   if (!allowed) return;
//
// No hidden side effects: the gate ONLY calls POST /api/entitlement/check
// (B.1) and optionally GET /api/v2/services/{sku} for the display name.
// The à la carte quote fetch (Cluster B.2) happens only when the user
// opts INTO the à la carte path inside the modal.
//
// Q5.4 + Q9.2 locks encoded:
//   - Soft-cap default: View 1 upgrade nudge → View 2 à la carte
//   - Premium-no-higher-tier: skip View 1 → straight to View 2
//   - Hard-cap: same two-view flow, "no auto-overage" copy
//   - not_subscribed / blocked: single-view, action-routed
//
// Q9.3 client-side conversion analytics fire on each transition so the
// `entitlement_events.user_action` column gets populated. Currently
// frontend-side trackEvent() only; if/when the audit collection wants
// the round-trip the events can POST to /entitlement/events/{id}/action.
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { trackEvent } from '@/lib/analytics';

// Display-name lookup cache (per-session). Avoids hitting /v2/services
// repeatedly when the same SKU is checked multiple times in a flow.
const _serviceMetaCache = new Map();

// All view names the modal renders. The reducer below validates
// transitions against this list.
export const VIEWS = Object.freeze({
  CLOSED: 'closed',
  LOADING: 'loading',
  NOT_SUBSCRIBED: 'not_subscribed',
  PAST_DUE: 'past_due',
  PINCODE_UNSERVED: 'pincode_unserved',
  SERVICE_INACTIVE: 'service_inactive',
  UPGRADE_REQUIRED: 'upgrade_required',
  CAP_UPGRADE: 'cap_upgrade',       // Q5.4 View 1 (soft + hard share copy via decision)
  CAP_ALACARTE: 'cap_alacarte',     // Q5.4 View 2 (or Premium-direct, or à la carte-only)
  ALACARTE_LOADING: 'alacarte_loading',
  ERROR: 'error',
});

const EntitlementContext = createContext(null);

// ─── Outcome → initial view router ──────────────────────────────────────

// Decide which view to open based on the EntitlementDecision from B.1.
function routeOutcome(decision) {
  if (decision.outcome === 'not_subscribed') return VIEWS.NOT_SUBSCRIBED;
  if (decision.outcome === 'blocked') {
    switch (decision.blocked_reason) {
      case 'past_due':           return VIEWS.PAST_DUE;
      case 'pincode_unserved':   return VIEWS.PINCODE_UNSERVED;
      case 'service_inactive':   return VIEWS.SERVICE_INACTIVE;
      case 'upgrade_required':   return VIEWS.UPGRADE_REQUIRED;
      default:                   return VIEWS.SERVICE_INACTIVE;
    }
  }
  // soft_cap + hard_cap routing per Q5.4 + Q9.2.
  if (decision.outcome === 'soft_cap' || decision.outcome === 'hard_cap') {
    // Premium-no-higher-tier OR à la carte-only services skip view 1.
    if (!decision.upgrade_to_tier && decision.alacarte_available) {
      return VIEWS.CAP_ALACARTE;
    }
    if (decision.upgrade_to_tier) {
      return VIEWS.CAP_UPGRADE;
    }
    // No upgrade target AND no à la carte → upgrade_required state.
    return VIEWS.UPGRADE_REQUIRED;
  }
  // Fallback. Shouldn't happen if backend contract holds.
  return VIEWS.ERROR;
}

// ─── Provider ───────────────────────────────────────────────────────────

export function EntitlementProvider({ children }) {
  const { api } = useAuth();
  // Quote currency follows the CurrencySwitcher selection so the user
  // sees the modal price in the same currency as the rest of the UI.
  // Backend SUPPORTED_CURRENCIES = USD/INR/AUD/GBP/SGD/CAD/EUR — same set
  // CurrencyContext seeds, so currencyCode is always valid.
  const { currencyCode } = useCurrency();
  const [view, setView] = useState(VIEWS.CLOSED);
  const [decision, setDecision] = useState(null);
  const [serviceMeta, setServiceMeta] = useState(null);
  const [quote, setQuote] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  // The pending resolver for the current check() call. We hold it on a
  // ref so the modal's button handlers can resolve it without depending
  // on stale closures.
  const resolverRef = useRef(null);
  // Tracks what we passed to check() so the à la carte path can
  // re-quote without the caller re-specifying.
  const lastInputRef = useRef(null);

  const resolveAndClose = useCallback((value, userAction) => {
    if (decision && userAction) {
      // Q9.3 client funnel — the backend audit row exists; we tag the
      // user-action client-side for analytics conversion attribution.
      trackEvent('entitlement_user_action', {
        outcome: decision.outcome,
        blocked_reason: decision.blocked_reason,
        service_sku: decision.service_sku,
        action: userAction,
      });
    }
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
    setView(VIEWS.CLOSED);
    setDecision(null);
    setQuote(null);
    setServiceMeta(null);
    setErrorMessage(null);
  }, [decision]);

  // ─── Public API: check() ──────────────────────────────────────────────
  const check = useCallback(
    async ({ service_sku, pincode = null, family_member_id = null }) => {
      if (!service_sku) {
        throw new Error('entitlement check: service_sku is required');
      }
      // Guard against re-entrancy while another check is in flight.
      if (resolverRef.current) {
        return false;
      }
      lastInputRef.current = { service_sku, pincode, family_member_id };
      setView(VIEWS.LOADING);

      // Kick off display-name fetch in parallel (cheap; cached).
      const metaPromise = (async () => {
        if (_serviceMetaCache.has(service_sku)) {
          return _serviceMetaCache.get(service_sku);
        }
        try {
          // Public services endpoint returns the meta (no auth needed)
          const res = await api.get('/v2/services');
          const found = (res.data || []).find((s) => s.sku === service_sku);
          if (found) _serviceMetaCache.set(service_sku, found);
          return found || null;
        } catch {
          return null;
        }
      })();

      // The promise we return — resolved by resolveAndClose().
      const decisionPromise = new Promise((resolve) => {
        resolverRef.current = resolve;
      });

      try {
        const checkRes = await api.post('/entitlement/check', {
          service_sku,
          pincode,
          family_member_id,
        });
        const d = checkRes.data;
        setDecision(d);
        setServiceMeta(await metaPromise);

        trackEvent('entitlement_check_result', {
          outcome: d.outcome,
          blocked_reason: d.blocked_reason,
          plan_key: d.plan_key,
          service_sku,
        });

        if (d.outcome === 'allow') {
          // Transparent allow — close modal, resolve true. No event
          // tracking on allow (Q9.3 backend doesn't log allow either).
          resolveAndClose(true, null);
          return decisionPromise;
        }

        setView(routeOutcome(d));
      } catch (err) {
        setErrorMessage(
          err.response?.data?.detail ||
            err.message ||
            'Failed to check eligibility. Please try again.'
        );
        setView(VIEWS.ERROR);
        trackEvent('entitlement_check_failed', {
          service_sku,
          error: err.message,
        });
      }

      return decisionPromise;
    },
    [api, resolveAndClose]
  );

  // ─── Internal: fetch alacarte quote ────────────────────────────────────
  // Called when user opts into à la carte path. Hits Cluster B.2.
  const fetchQuote = useCallback(async () => {
    const input = lastInputRef.current;
    if (!input) return;
    setView(VIEWS.ALACARTE_LOADING);
    try {
      const res = await api.post('/alacarte/quote', {
        service_sku: input.service_sku,
        pincode: input.pincode || undefined,
        family_member_id: input.family_member_id || undefined,
        // Follow the user's CurrencySwitcher selection; CurrencyContext
        // defaults to 'USD' if no IP-based detection succeeded.
        currency_display: currencyCode || 'USD',
      });
      setQuote(res.data);
      setView(VIEWS.CAP_ALACARTE);
    } catch (err) {
      setErrorMessage(
        err.response?.data?.detail?.message ||
          err.response?.data?.detail ||
          'Could not load à la carte price. Please try again.'
      );
      setView(VIEWS.ERROR);
    }
  }, [api, currencyCode]);

  // ─── Internal: transition from view 1 (upgrade) to view 2 (à la carte) ─
  // Q5.4 lock: when user dismisses the upgrade view, slide to à la carte.
  const dismissUpgradeShowAlacarte = useCallback(async () => {
    trackEvent('entitlement_user_action', {
      action: 'dismissed_upgrade',
      service_sku: decision?.service_sku,
    });
    if (decision?.alacarte_available) {
      await fetchQuote();
    } else {
      // Shouldn't happen if routeOutcome was correct; defensive fallback.
      resolveAndClose(false, 'abandoned');
    }
  }, [decision, fetchQuote, resolveAndClose]);

  // ─── Internal: close modal without action (dismiss) ────────────────────
  const dismiss = useCallback(
    (action = 'abandoned') => {
      resolveAndClose(false, action);
    },
    [resolveAndClose]
  );

  // ─── Internal: retry after error ───────────────────────────────────────
  const retry = useCallback(async () => {
    if (!lastInputRef.current) {
      resolveAndClose(false, 'abandoned');
      return;
    }
    // Re-run the original check with the cached inputs.
    const input = lastInputRef.current;
    resolverRef.current = null; // allow re-entry
    setErrorMessage(null);
    const result = await check(input);
    return result;
  }, [check, resolveAndClose]);

  // Exposed-to-modal API. Keep it narrow — anything not in here forces
  // the modal to lean on local state only.
  const modalApi = useMemo(
    () => ({
      view,
      decision,
      serviceMeta,
      quote,
      errorMessage,
      // user actions
      dismiss,
      dismissUpgradeShowAlacarte,
      fetchQuote,
      retry,
    }),
    [
      view,
      decision,
      serviceMeta,
      quote,
      errorMessage,
      dismiss,
      dismissUpgradeShowAlacarte,
      fetchQuote,
      retry,
    ]
  );

  const value = useMemo(
    () => ({
      check,
      __modalApi: modalApi, // consumed only by <EntitlementModal>
    }),
    [check, modalApi]
  );

  return (
    <EntitlementContext.Provider value={value}>{children}</EntitlementContext.Provider>
  );
}

// ─── Hooks ──────────────────────────────────────────────────────────────

export function useEntitlementGate() {
  const ctx = useContext(EntitlementContext);
  if (!ctx) {
    throw new Error('useEntitlementGate must be used inside <EntitlementProvider>');
  }
  return { check: ctx.check };
}

// Internal hook for the modal — exposes the modal-side state machine.
export function useEntitlementModalApi() {
  const ctx = useContext(EntitlementContext);
  if (!ctx) {
    throw new Error('useEntitlementModalApi must be used inside <EntitlementProvider>');
  }
  return ctx.__modalApi;
}
